/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-09-05 15:38:04
 **/

import { nanoid } from "nanoid";
import { Properties } from "@/frameworks/libraries/properties";
import { MediaUtility } from "@/frameworks/libraries/media";

const LOGTAG = "#DummyStream#";
const DummyStreams = new Map<string, DummyStream>();

export default class DummyStream {
	public static register(dummyStream: DummyStream) {
		DummyStreams.set(dummyStream.uniqueID, dummyStream);
		console.debug(LOGTAG, "Registrered dummy stream", dummyStream);
	}

	public static unregister(dummyStream: DummyStream) {
		dummyStream.destroy();
		DummyStreams.delete(dummyStream.uniqueID);
		console.debug(LOGTAG, "Unregistrered dummy stream", dummyStream);
	}

	private _stream: MediaStream | null = null;
	private _canvas: HTMLCanvasElement | null = null;
	public readonly uniqueID = nanoid().toLowerCase();

	constructor() {
		DummyStream.register(this);
	}

	public get stream() {
		return this._stream;
	}

	public get canvas_id() {
		return `dummy-${this.uniqueID}`;
	}

	public create(width: number, height: number) {
		if (this._canvas) {
			this.destroy();
		}
		if (this._canvas === null) {
			this._canvas = document.getElementById(this.canvas_id) as HTMLCanvasElement;
			if (!this._canvas) {
				this._canvas = document.createElement("canvas");
				this._canvas.id = this.canvas_id;
				this._canvas.width = width;
				this._canvas.height = height;

				this._canvas.style.position = "fixed";
				this._canvas.style.left = "0";
				this._canvas.style.top = "0";
				this._canvas.style.zIndex = "-99999";
				this._canvas.style.display = "none";

				document.body.appendChild(this._canvas);
			}
		}

		this._draw_blank_screen();

		this._stream = new MediaStream(this._canvas.captureStream(5));
		console.debug(LOGTAG, `canvas id: ${this.canvas_id}, stream.id: ${this._stream.id}`);
		return this._stream;
	}

	public destroy() {
		MediaUtility.close(this.stream);

		if (this._canvas) {
			document.body.removeChild(this._canvas);
			this._canvas = null;
		}
	}

	private _draw_blank_screen() {
		if (this._canvas !== null) {
			const context = this._canvas.getContext("2d")!;
			context.beginPath();
			context.fillStyle = "#333";
			context.fillRect(0, 0, this._canvas.width, this._canvas.height);
			context.closePath();

			if (!Properties.property.isTabHidden) {
				requestAnimationFrame(this._draw_blank_screen.bind(this));
			} else {
				window.setTimeout(this._draw_blank_screen.bind(this), 100);
			}
		}
	}
}
